import React, { useCallback, useEffect, useState } from "react";
import { orderBy } from "lodash";
import TodosTable from "../../MyTodoList/TodosTable";
import TodoForm from "../../MyTodoList/TodoForm";
import "./TodoListTab.css";
import { getUsers } from "../../../services/auth";
import { ADMIN_ROLE } from "../../../constants";
import { createTodo, updateTodo, getTodos } from "../../../services/todos";
import { buildTodoSubmission } from "./todoSubmission";

import momentTZ from "moment-timezone";
import { DEFAULT_TIMEZONE } from "../../../constants";
// The service only has the HTTP client's message (e.g. "Request failed with
// status code 500"), which tells a coordinator nothing; the detail is logged.
const TODO_SAVE_FAILED = "The todo could not be saved. Please try again.";

const TodoListTab = ({ caId, clientFullName }) => {
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmitTodo = async (formValues) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      // Editing keeps the todo's original created date and creator.
      const { todoId, data } = buildTodoSubmission({
        formValues,
        selectedTodo,
        caId,
        clientFullName,
        createdAt: momentTZ().tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD HH:mm"),
      });
      // The services catch request failures and resolve with { success: false },
      // so a failed save has to be checked for here rather than caught.
      const result = todoId ? await updateTodo(todoId, data) : await createTodo(data);
      if (!result.success) {
        setSubmitError(TODO_SAVE_FAILED);
        return;
      }

      await fetchTodos();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTodoStatus = async (id, newStatus) => {
    const result = await updateTodo(id, { status: newStatus });
    if (result.success) {
      await fetchTodos();
    }
  };

  const handleEditTodo = (id) => {
    const selected = todos.find((todo) => todo.id === id);
    setSelectedTodo(selected);
    setSubmitError("");
  };

  const handleResetTodo = () => {
    setSelectedTodo(null);
    setSubmitError("");
  };

  useEffect(() => {
    async function fetchUsers() {
      const users = await getUsers(ADMIN_ROLE);
      setAllUsers(users || []);
      setUsers(users?.filter((user) => !user.isITStaff && !user.disabled) || []);
    }
    fetchUsers();
  }, []);

  const fetchTodos = useCallback(async () => {
    const result = await getTodos({ client_appointment_id: caId });
    if (result.success && result.data) {
      // Sort by due_date ascending (same as before)
      const sortedTodos = orderBy(result.data, ["due_date"], ["asc"]);
      setTodos(sortedTodos);
    } else {
      console.error("Error fetching todos:", result.error);
      setTodos([]);
    }
  }, [caId]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  return (
    <>
      <TodoForm
        isSubmitting={isSubmitting}
        caId={caId}
        todo={selectedTodo}
        assignees={users}
        onSubmit={handleSubmitTodo}
        onReset={handleResetTodo}
      />
      {submitError && <p className="help is-danger">{submitError}</p>}
      <TodosTable
        todos={todos}
        selectedId={selectedTodo && selectedTodo.id}
        assignees={users}
        users={allUsers}
        onEdit={handleEditTodo}
        handleUpdateTodoStatus={handleUpdateTodoStatus}
      />
    </>
  );
};

export default TodoListTab;
